// ============================================================
// Datei: frontend/src/pages/Login.tsx
// Login, Register & Email-Verifizierung
// ============================================================
import React, { useState, useRef, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { useToast } from '../components/Toast';
import { getErrorMessage } from '../services/api';

type Screen = 'login' | 'register' | 'verify';

const Login: React.FC = () => {
  const { login, register, verify, resendCode } = useAuth();
  const toast = useToast();

  const [screen, setScreen] = useState<Screen>('login');
  const [email, setEmail] = useState('');
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [code, setCode] = useState(['', '', '', '', '', '']);
  const [loading, setLoading] = useState(false);
  const [verifyEmail, setVerifyEmail] = useState('');

  const codeRefs = useRef<(HTMLInputElement | null)[]>([]);

  // Auto-focus erstes Code-Feld
  useEffect(() => {
    if (screen === 'verify') {
      setTimeout(() => codeRefs.current[0]?.focus(), 100);
    }
  }, [screen]);

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email.trim()) { toast.warning('Enter email', 'Please enter your email address.'); return; }
    if (!password) { toast.warning('Enter password', 'Please enter your password.'); return; }

    setLoading(true);
    try {
      await login(email.trim(), password);
      toast.success('Welcome back!');
    } catch (err: any) {
      const msg = getErrorMessage(err);
      if (err?.response?.status === 403) {
        // Nicht verifiziert
        toast.warning('Email not verified', 'Please confirm your account with the code from your email.');
        setVerifyEmail(email.trim());
        setScreen('verify');
      } else {
        toast.error('Login failed', msg);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleRegister = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email.trim() || !email.includes('@')) { toast.warning('Invalid email', 'Please enter a valid email.'); return; }
    if (!username.trim() || username.trim().length < 2) { toast.warning('Username too short', 'At least 2 characters.'); return; }
    if (password.length < 6) { toast.warning('Password too short', 'At least 6 characters.'); return; }
    if (password !== confirmPassword) { toast.error('Passwords do not match', 'The passwords do not match.'); return; }

    setLoading(true);
    try {
      const msg = await register(email.trim(), username.trim(), password);
      toast.success('Account created!', msg);
      setVerifyEmail(email.trim());
      setScreen('verify');
    } catch (err: any) {
      toast.error('Registration failed', getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  const handleCodeInput = (index: number, value: string) => {
    if (value.length > 1) value = value[value.length - 1];
    if (value && !/^\d$/.test(value)) return;

    const newCode = [...code];
    newCode[index] = value;
    setCode(newCode);

    // Auto-focus next
    if (value && index < 5) {
      codeRefs.current[index + 1]?.focus();
    }

    // Auto-submit wenn komplett
    if (value && index === 5 && newCode.every(c => c)) {
      submitCode(newCode.join(''));
    }
  };

  const handleCodeKeyDown = (index: number, e: React.KeyboardEvent) => {
    if (e.key === 'Backspace' && !code[index] && index > 0) {
      codeRefs.current[index - 1]?.focus();
    }
  };

  const handleCodePaste = (e: React.ClipboardEvent) => {
    const pasted = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6);
    if (pasted.length === 6) {
      const newCode = pasted.split('');
      setCode(newCode);
      codeRefs.current[5]?.focus();
      submitCode(pasted);
      e.preventDefault();
    }
  };

  const submitCode = async (codeStr?: string) => {
    const c = codeStr || code.join('');
    if (c.length !== 6) { toast.warning('Code incomplete', 'Please enter all 6 digits.'); return; }

    setLoading(true);
    try {
      await verify(verifyEmail, c);
      toast.success('Email verified!', 'Welcome to Trading Engine!');
    } catch (err: any) {
      toast.error('Invalid code', getErrorMessage(err));
      setCode(['', '', '', '', '', '']);
      codeRefs.current[0]?.focus();
    } finally {
      setLoading(false);
    }
  };

  const handleResend = async () => {
    setLoading(true);
    try {
      const msg = await resendCode(verifyEmail);
      toast.success('Code sent!', msg);
      setCode(['', '', '', '', '', '']);
      codeRefs.current[0]?.focus();
    } catch (err: any) {
      toast.error('Fehler', getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={st.wrapper}>
      <div style={st.bgGlow} />

      <div style={st.card}>
        {/* Logo */}
        <div style={st.logo}>
          <span style={{ fontSize: 32 }}>📊</span>
          <span style={{ fontSize: 22, fontWeight: 800, color: '#fff' }}>Trading Engine</span>
          <span style={{ fontSize: 11, fontWeight: 600, color: '#8b5cf6', background: '#8b5cf622', padding: '2px 8px', borderRadius: 8 }}>v2.2</span>
        </div>

        {/* ========== LOGIN ========== */}
        {screen === 'login' && (
          <>
            <h2 style={st.title}>Welcome back</h2>
            <p style={st.subtitle}>Log in to continue</p>

            <form onSubmit={handleLogin} style={st.form}>
              <div style={st.fieldGroup}>
                <label style={st.label}>E-Mail</label>
                <input type="email" value={email} onChange={e => setEmail(e.target.value)}
                  placeholder="deine@email.com" style={st.input} autoFocus />
              </div>
              <div style={st.fieldGroup}>
                <label style={st.label}>Password</label>
                <input type="password" value={password} onChange={e => setPassword(e.target.value)}
                  placeholder="••••••••" style={st.input} />
              </div>
              <button type="submit" style={{ ...st.submitBtn, opacity: loading ? 0.6 : 1 }} disabled={loading}>
                {loading ? '⏳ Please wait...' : '🔓 Log in'}
              </button>
            </form>

            <div style={st.toggle}>
              <span style={{ color: '#64748b' }}>No account yet?</span>
              <button onClick={() => { setScreen('register'); }} style={st.toggleBtn}>Register</button>
            </div>
          </>
        )}

        {/* ========== REGISTER ========== */}
        {screen === 'register' && (
          <>
            <h2 style={st.title}>Create Account</h2>
            <p style={st.subtitle}>Register and confirm your email</p>

            <form onSubmit={handleRegister} style={st.form}>
              <div style={st.fieldGroup}>
                <label style={st.label}>E-Mail</label>
                <input type="email" value={email} onChange={e => setEmail(e.target.value)}
                  placeholder="deine@email.com" style={st.input} autoFocus />
              </div>
              <div style={st.fieldGroup}>
                <label style={st.label}>Username</label>
                <input type="text" value={username} onChange={e => setUsername(e.target.value)}
                  placeholder="Trader123" style={st.input} />
              </div>
              <div style={st.fieldGroup}>
                <label style={st.label}>Password</label>
                <input type="password" value={password} onChange={e => setPassword(e.target.value)}
                  placeholder="Min. 6 characters" style={st.input} />
              </div>
              <div style={st.fieldGroup}>
                <label style={st.label}>Confirm password</label>
                <input type="password" value={confirmPassword} onChange={e => setConfirmPassword(e.target.value)}
                  placeholder="••••••••" style={st.input} />
              </div>
              <button type="submit" style={{ ...st.submitBtn, opacity: loading ? 0.6 : 1 }} disabled={loading}>
                {loading ? '⏳ Please wait...' : '🚀 Register'}
              </button>
            </form>

            <div style={st.toggle}>
              <span style={{ color: '#64748b' }}>Already registered?</span>
              <button onClick={() => setScreen('login')} style={st.toggleBtn}>To login</button>
            </div>
          </>
        )}

        {/* ========== VERIFY CODE ========== */}
        {screen === 'verify' && (
          <>
            <div style={{ textAlign: 'center', marginBottom: 8 }}>
              <div style={{ fontSize: 48, marginBottom: 12 }}>📧</div>
              <h2 style={st.title}>Verify Email</h2>
              <p style={st.subtitle}>
                We sent a 6-digit code to<br />
                <strong style={{ color: '#8b5cf6' }}>{verifyEmail}</strong> gesendet.
              </p>
              <p style={{ color: '#475569', fontSize: 12, marginTop: 8 }}>
                💡 The code is in the backend terminal (cargo run Ausgabe)
              </p>
            </div>

            {/* 6-Digit Code Input */}
            <div style={{ display: 'flex', justifyContent: 'center', gap: 10, margin: '24px 0' }}>
              {code.map((digit, i) => (
                <input
                  key={i}
                  ref={el => codeRefs.current[i] = el}
                  type="text"
                  inputMode="numeric"
                  maxLength={1}
                  value={digit}
                  onChange={e => handleCodeInput(i, e.target.value)}
                  onKeyDown={e => handleCodeKeyDown(i, e)}
                  onPaste={i === 0 ? handleCodePaste : undefined}
                  style={{
                    width: 50, height: 58,
                    background: digit ? '#8b5cf622' : '#0f172a',
                    border: `2px solid ${digit ? '#8b5cf6' : '#1e293b'}`,
                    borderRadius: 12,
                    color: '#fff',
                    fontSize: 24,
                    fontWeight: 700,
                    textAlign: 'center',
                    outline: 'none',
                    transition: 'all 0.2s',
                  }}
                />
              ))}
            </div>

            <button
              onClick={() => submitCode()}
              style={{ ...st.submitBtn, opacity: loading ? 0.6 : 1 }}
              disabled={loading || code.some(c => !c)}
            >
              {loading ? '⏳ Verifying...' : '✅ Confirm code'}
            </button>

            <div style={{ textAlign: 'center', marginTop: 20 }}>
              <button onClick={handleResend} style={st.toggleBtn} disabled={loading}>
                📧 Resend code
              </button>
              <span style={{ color: '#334155', margin: '0 8px' }}>|</span>
              <button onClick={() => setScreen('login')} style={st.toggleBtn}>
                ← Back to login
              </button>
            </div>
          </>
        )}

        {/* Features */}
        {screen !== 'verify' && (
          <div style={st.features}>
            <div style={st.feature}><span>📈</span> Trade Tracking</div>
            <div style={st.feature}><span>📊</span> S&P 500 Benchmark</div>
            <div style={st.feature}><span>🤖</span> ML-Analyse</div>
            <div style={st.feature}><span>📓</span> Trading Journal</div>
          </div>
        )}
      </div>
    </div>
  );
};

// ============================================================
// Styles
// ============================================================
const st: Record<string, React.CSSProperties> = {
  wrapper: { minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#0a0e1a', position: 'relative', overflow: 'hidden', padding: 20 },
  bgGlow: { position: 'absolute', width: 600, height: 600, borderRadius: '50%', background: 'radial-gradient(circle, rgba(139,92,246,0.15) 0%, transparent 70%)', top: '-10%', right: '-10%', pointerEvents: 'none' },
  card: { background: '#111827', border: '1px solid #1e293b', borderRadius: 20, padding: '40px 36px', width: '100%', maxWidth: 440, position: 'relative' as const, zIndex: 1, boxShadow: '0 20px 60px rgba(0,0,0,0.5)' },
  logo: { display: 'flex', alignItems: 'center', gap: 10, marginBottom: 28, justifyContent: 'center' },
  title: { color: '#f1f5f9', fontSize: 24, fontWeight: 700, margin: '0 0 6px 0', textAlign: 'center' as const },
  subtitle: { color: '#64748b', fontSize: 14, margin: '0 0 24px 0', textAlign: 'center' as const, lineHeight: 1.5 },
  form: { display: 'flex', flexDirection: 'column' as const, gap: 16 },
  fieldGroup: { display: 'flex', flexDirection: 'column' as const, gap: 6 },
  label: { color: '#94a3b8', fontSize: 13, fontWeight: 600 },
  input: { background: '#0f172a', border: '1px solid #1e293b', borderRadius: 10, padding: '12px 14px', color: '#e2e8f0', fontSize: 15, outline: 'none', width: '100%', boxSizing: 'border-box' as const },
  submitBtn: { background: 'linear-gradient(135deg, #8b5cf6 0%, #6d28d9 100%)', color: '#fff', border: 'none', borderRadius: 10, padding: '14px 0', fontSize: 16, fontWeight: 700, cursor: 'pointer', marginTop: 6, width: '100%' },
  toggle: { display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6, marginTop: 20, fontSize: 14 },
  toggleBtn: { background: 'none', border: 'none', color: '#8b5cf6', fontWeight: 600, cursor: 'pointer', fontSize: 14, textDecoration: 'underline' },
  features: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginTop: 28, paddingTop: 20, borderTop: '1px solid #1e293b' },
  feature: { color: '#64748b', fontSize: 12, display: 'flex', alignItems: 'center', gap: 6 },
};

export default Login;